/** Musey ↔ lyric-stage iframe postMessage contract (mirrored; keep in sync with Musey/web). */

export const PROTOCOL_VERSION = 1

export const CHANNEL = 'musey-lyric-stage'

/** Parent → Stage */
export const M2S = {
  SESSION_INIT: 'SESSION_INIT',
  SESSION_END: 'SESSION_END',
  TRACK_CHANGED: 'TRACK_CHANGED',
  QUEUE_UPDATED: 'QUEUE_UPDATED',
  TIME_TICK: 'TIME_TICK',
  AUDIO_BANDS: 'AUDIO_BANDS',
  PV_STATUS: 'PV_STATUS',
} as const

/** Stage → Parent */
export const S2M = {
  READY: 'READY',
  EXIT: 'EXIT',
  CONTROL: 'CONTROL',
  START_PV: 'START_PV',
  CANCEL_PV: 'CANCEL_PV',
} as const

export type M2SType = (typeof M2S)[keyof typeof M2S]
export type S2MType = (typeof S2M)[keyof typeof S2M]

export type LyricPayload =
  | {
    kind: 'sync'
    title?: string
    artist?: string
    instrumental?: boolean
    lyricist?: string
    composer?: string
    lines: Array<{
      text?: string
      start?: number
      end?: number
      words?: Array<{ text?: string; start?: number; end?: number }> | null
    }>
    words?: Array<{ text?: string; start?: number; end?: number }> | null
  }
  | {
    kind: 'plain'
    title?: string
    artist?: string
    instrumental?: boolean
    lyricist?: string
    composer?: string
    text: string
  }
  | {
    kind: 'empty'
    title?: string
    artist?: string
    instrumental?: boolean
    lyricist?: string
    composer?: string
  }

export type QueueItem = { id: string; title: string }

export type SessionPayload = {
  title?: string | null
  artist?: string | null
  durationMs: number
  coverUrl?: string | null
  lyricPayload: LyricPayload
  queue?: QueueItem[]
  currentTrackId?: string | null
  paused: boolean
  positionMs: number
  recordSupported?: boolean
}

export type TimeTickPayload = {
  positionMs: number
  sampledAtMs: number
  paused: boolean
}

export type AudioBandsPayload = {
  power: number
  bass: number
  lowMid: number
  mid: number
  vocal: number
  treble: number
}

export type PvStatusPayload = {
  phase: string
  countdown: number | null
  elapsed: number
  duration: number
  error: string | null
}

export type ControlPayload =
  | { action: 'play' | 'pause' | 'toggle' | 'prev' | 'next' }
  | { action: 'seek'; positionMs: number }
  | { action: 'selectTrack'; trackId: string }

export type StageEnvelope = {
  channel: typeof CHANNEL
  v: number
  type: string
  payload?: unknown
}

export function isStageMessage(data: unknown): data is StageEnvelope {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    d.channel === CHANNEL
    && Number(d.v) === PROTOCOL_VERSION
    && typeof d.type === 'string'
  )
}

export function makeEnvelope(type: string, payload: unknown = {}) {
  return {
    channel: CHANNEL,
    v: PROTOCOL_VERSION,
    type,
    payload,
  }
}
